import React, { useState, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Progress,
  Icon,
  Checkbox,
  Table,
  Input,
  Pagination,
} from "semantic-ui-react";
import html2pdf from "html2pdf.js";
import LibraryCardContent from "@authentication/pages/Profile/ProfileForm/LibraryCardContent/LibraryCardContent";
import BackCardContent from "@authentication/pages/Profile/ProfileForm/LibraryCardContent/BackCardContent";
import { profileApi } from "@api/profile/profile";
import { configurableApi } from "@api/configurables/configurables";
import { getFullImageUrl } from "@utils/mobileUtils";

const CONFIG = {
  CARDS_PER_PAGE: 8,
  STUDENTS_PER_PAGE: 5,
  CARD: { WIDTH: 370, HEIGHT: 255, SCALE: 0.525, ORIG_W: 700, ORIG_H: 500 },
  GRID: { COLS: 2, ROWS: 4, GAP: 6 },
  DELAYS: { RENDER: 100, GROUP: 400, FINAL: 800, SUCCESS: 1500 },
  IMG_TIMEOUT: 8000,
  DEFAULT_LIBRARIAN: "UNIVERSITY LIBRARIAN",
};

const STYLES = {
  card: {
    width: 700,
    height: 500,
    border: "2px solid #333",
    borderRadius: 8,
    background: "#f8f9fa",
    fontFamily: "Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    padding: "20px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  leftLogo: {
    width: 120,
    height: 120,
    objectFit: "contain",
    borderRadius: "50%",
  },
  rightLogo: {
    width: 120,
    height: 120,
    objectFit: "contain",
    borderRadius: "50%",
  },
  universityInfo: { textAlign: "center", flex: 1, margin: "0 10px" },
  universityName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#333",
    margin: 0,
    letterSpacing: "0.5px",
  },
  universityLocation: {
    fontSize: 14,
    color: "#666",
    margin: "2px 0 8px 0",
    fontWeight: "800",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#333",
    margin: 0,
    letterSpacing: "1px",
  },
  mainContent: { display: "flex", flex: 1, gap: "30px" },
  leftSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  rightSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  fieldGroup: { marginBottom: 25 },
  fieldLabel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#333",
    marginBottom: 8,
    letterSpacing: "0.5px",
  },
  fieldLine: {
    borderBottom: "1.5px solid #333",
    height: 24,
    minHeight: 24,
    display: "flex",
    alignItems: "flex-end",
    paddingBottom: 2,
  },
  fieldValue: {
    fontSize: 18,
    color: "#000",
    fontWeight: "500",
    width: "100%",
    minHeight: 20,
    display: "flex",
    alignItems: "flex-end",
    paddingBottom: 2,
    letterSpacing: "0.5px",
  },
  photoContainer: {
    width: 160,
    height: 160,
    border: "2px solid #333",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: "#666",
    fontWeight: "bold",
    overflow: "hidden",
    marginTop: 45,
  },
  photoImage: { width: "100%", height: "100%", objectFit: "cover" },
  librarianSection: { textAlign: "center", marginTop: "auto" },
  librarianName: { fontWeight: "bold", color: "#333", textAlign: "center" },
  librarianTitle: {
    fontSize: 14,
    color: "#666",
    margin: "2px 0 0 0",
    letterSpacing: "0.3px",
    fontWeight: "600",
  },
  barcode: {
    display: "block",
    margin: "0 auto",
    padding: "8px 0",
    backgroundColor: "fff",
    border: "1px solid #ddd",
    borderRadius: "4px",
    maxWidth: "fit-content",
  },
};

const waitForImages = (container) =>
  new Promise((resolve) => {
    const images = container?.querySelectorAll("img") || [];
    if (!images.length) return resolve();
    let count = 0;
    const check = () => ++count >= images.length && resolve();
    images.forEach((img) =>
      img.complete && img.naturalHeight > 0
        ? check()
        : (img.onload = img.onerror = check)
    );
    setTimeout(resolve, CONFIG.IMG_TIMEOUT);
  });

const filterStudents = (students, query) =>
  !query.trim()
    ? students
    : students.filter((s) =>
        [s.full_name || s.name, s.library_no, s.program].some((f) =>
          (f || "").toLowerCase().includes(query.toLowerCase())
        )
      );

const paginate = (data, page, size) => {
  const start = (page - 1) * size;
  return {
    items: data.slice(start, start + size),
    start,
    end: start + size,
    totalPages: Math.ceil(data.length / size),
  };
};

const chunk = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );

const fetchLibrarianName = async () => {
  try {
    const res = await configurableApi.getValue("Library Name");
    return (res?.data?.trim() || CONFIG.DEFAULT_LIBRARIAN).toUpperCase();
  } catch {
    return CONFIG.DEFAULT_LIBRARIAN;
  }
};

const fetchStudentImage = async (student) => {
  try {
    if (student.image_url) return getFullImageUrl(student.image_url);
    if (student.id) {
      const info = await profileApi.getUserImageInfo(student.id);
      if (info?.image_url) return getFullImageUrl(info.image_url);
    }
  } catch (err) {
    console.error(`Image fetch error for ${student.id}:`, err);
  }
  return null;
};

const fetchAllImages = async (students) => {
  const results = await Promise.all(
    students.map(async (s) => ({
      id: s.id,
      url: await fetchStudentImage(s),
    }))
  );
  return Object.fromEntries(results.map((r) => [r.id, r.url]));
};

const renderLogo = (src, alt, style) =>
  React.createElement("img", {
    src,
    alt,
    style,
    crossOrigin: "anonymous",
    onError: (e) => {
      e.target.style.display = "none";
    },
  });

const createPhotoElement = (url) => {
  if (!url) {
    return React.createElement(
      "div",
      {
        style: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          gap: "4px",
        },
      },
      [
        React.createElement(
          "div",
          {
            key: "no",
            style: { fontSize: "14px", color: "#666", fontWeight: "bold" },
          },
          "NO PHOTO"
        ),
        React.createElement(
          "div",
          {
            key: "err",
            style: { fontSize: "10px", color: "#d32f2f" },
          },
          "No Uploaded Photo"
        ),
      ]
    );
  }

  return React.createElement("img", {
    src: url,
    alt: "Student",
    style: STYLES.photoImage,
    crossOrigin: "anonymous",
    loading: "eager",
    onError: (e) => {
      const parent = e.target.parentNode;
      if (parent) {
        e.target.remove();
        const fallback = document.createElement("div");
        fallback.style.cssText =
          "display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;font-size:14px;color:#666;font-weight:bold;gap:4px;";
        fallback.innerHTML =
          '<div>NO PHOTO</div><div style="font-size:10px;color:#d32f2f;">Failed to Load</div>';
        parent.appendChild(fallback);
      }
    },
  });
};

const createCard = (student, imgUrl, isBack, libName) => {
  const card = document.createElement("div");
  Object.assign(card.style, {
    width: `${CONFIG.CARD.WIDTH}px`,
    height: `${CONFIG.CARD.HEIGHT}px`,
    border: "1px solid #333",
    borderRadius: "4px",
    background: "#f8f9fa",
    fontFamily: "Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
    display: "inline-block",
    boxSizing: "border-box",
  });
  const wrapper = document.createElement("div");
  Object.assign(wrapper.style, {
    transform: `scale(${CONFIG.CARD.SCALE})`,
    transformOrigin: "top left",
    width: `${CONFIG.CARD.ORIG_W}px`,
    height: `${CONFIG.CARD.ORIG_H}px`,
    overflow: "hidden",
    position: "absolute",
    top: 0,
    left: 0,
    paddingTop: "5px",
    paddingLeft: "13px",
    paddingRight: "13px",
    paddingBottom: "40px",
    boxSizing: "border-box",
  });
  const container = document.createElement("div");
  container.style.width = `${CONFIG.CARD.ORIG_W}px`;
  container.style.height = `${CONFIG.CARD.ORIG_H}px`;
  ReactDOM.render(
    isBack
      ? React.createElement(BackCardContent, { user: student, STYLES })
      : React.createElement(LibraryCardContent, {
          user: student,
          STYLES,
          renderLogo,
          renderPhoto: () => createPhotoElement(imgUrl),
          librarianName: libName || CONFIG.DEFAULT_LIBRARIAN,
          librarianTitle: CONFIG.DEFAULT_LIBRARIAN,
        }),
    container
  );
  setTimeout(
    () => (wrapper.innerHTML = container.innerHTML),
    CONFIG.DELAYS.RENDER
  );
  card.appendChild(wrapper);
  return card;
};

const createGridPage = (students, images, isBack, libName) => {
  const page = document.createElement("div");
  Object.assign(page.style, {
    width: "100%",
    paddingTop: "5mm",
    boxSizing: "border-box",
    pageBreakAfter: "always",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  });

  const title = document.createElement("h3");
  title.textContent = `Student Library Cards - ${isBack ? "Back" : "Front"}`;
  Object.assign(title.style, {
    textAlign: "center",
    marginBottom: "8px",
    fontSize: "16px",
    color: "#333",
    fontWeight: "bold",
  });

  const grid = document.createElement("div");
  Object.assign(grid.style, {
    display: "grid",
    gridTemplateColumns: `repeat(${CONFIG.GRID.COLS}, ${CONFIG.CARD.WIDTH}px)`,
    gridTemplateRows: `repeat(${CONFIG.GRID.ROWS}, ${CONFIG.CARD.HEIGHT}px)`,
    gap: `${CONFIG.GRID.GAP}px`,
    justifyContent: "center",
    width: `${
      CONFIG.GRID.COLS * (CONFIG.CARD.WIDTH + CONFIG.GRID.GAP) - CONFIG.GRID.GAP
    }px`,
  });

  let toRender = isBack ? [...students] : students;
  if (isBack) {
    const reordered = [];
    for (let r = 0; r < CONFIG.GRID.ROWS; r++) {
      const start = r * CONFIG.GRID.COLS;
      const rowStudents = toRender.slice(
        start,
        Math.min(start + CONFIG.GRID.COLS, toRender.length)
      );
      reordered.push(...rowStudents.reverse());
    }
    toRender = reordered;
  }

  toRender.forEach((s) => {
    const wrapper = document.createElement("div");
    wrapper.style.position = "relative";
    const card = createCard(s, images[s.id], isBack, libName);
    if (isBack) card.style.transform = "scaleX(1)";
    wrapper.appendChild(card);
    grid.appendChild(wrapper);
  });

  page.appendChild(title);
  page.appendChild(grid);
  return page;
};

const usePdfGen = (onComplete) => {
  const [isGen, setIsGen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");

  const generate = useCallback(
    async (students) => {
      if (!students?.length || isGen) return;
      setIsGen(true);
      setProgress(0);
      let temp = null;

      try {
        const libName = await fetchLibrarianName();
        setStatus("Fetching images...");
        const images = await fetchAllImages(students);

        temp = document.createElement("div");
        Object.assign(temp.style, {
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: "100%",
        });
        document.body.appendChild(temp);

        const pdf = document.createElement("div");
        pdf.style.fontFamily = "Arial, sans-serif";
        pdf.style.backgroundColor = "#fff";

        const groups = chunk(students, CONFIG.CARDS_PER_PAGE);
        const total = groups.length * 2;
        let step = 0;

        for (const grp of groups) {
          setStatus(
            `Processing group ${Math.floor(step / 2) + 1}/${groups.length}...`
          );
          pdf.appendChild(createGridPage(grp, images, false, libName));
          await new Promise((r) => setTimeout(r, CONFIG.DELAYS.GROUP));
          setProgress((++step / total) * 100);

          pdf.appendChild(createGridPage(grp, images, true, libName));
          await new Promise((r) => setTimeout(r, CONFIG.DELAYS.GROUP));
          setProgress((++step / total) * 100);
        }

        temp.appendChild(pdf);
        setStatus("Loading images...");
        await waitForImages(temp);
        await new Promise((r) => setTimeout(r, CONFIG.DELAYS.FINAL));

        setStatus("Generating PDF...");
        await html2pdf()
          .set({
            margin: [2, 2, 2, 2],
            filename: `bulk-library-cards-${
              new Date().toISOString().split("T")[0]
            }.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: {
              scale: 3,
              useCORS: true,
              letterRendering: true,
              allowTaint: true,
              backgroundColor: "#fff",
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          })
          .from(pdf)
          .save();

        setStatus("Complete!");
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
          setProgress(0);
          setStatus("");
        }, CONFIG.DELAYS.SUCCESS);
      } catch (err) {
        console.error("PDF gen failed:", err);
        alert(
          `PDF generation failed: ${
            err.message || "Unknown error"
          }. Please try again.`
        );
        setStatus("");
        setProgress(0);
      } finally {
        if (temp && document.body.contains(temp)) {
          document.body.removeChild(temp);
        }
        setIsGen(false);
      }
    },
    [isGen, onComplete]
  );

  return { isGen, progress, status, generate };
};

const BulkStudentCardDownloader = ({ open, onClose, studentData, loading }) => {
  const problematicUserIds = ["2", "7", "12", "13", "14", "15", "41"];
  const validStudentData = studentData.filter(
    (student) => !problematicUserIds.includes(student.id)
  );

  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const handleComplete = useCallback(() => {
    setSelected([]);
    onClose();
  }, [onClose]);
  const { isGen, progress, status, generate } = usePdfGen(handleComplete);

  const filtered = useMemo(
    () => filterStudents(validStudentData, search),
    [studentData, search]
  );
  const paged = useMemo(
    () => paginate(filtered, page, CONFIG.STUDENTS_PER_PAGE),
    [filtered, page]
  );

  const isAllSel = selected.length === filtered.length && filtered.length > 0;
  const isPageSel =
    paged.items.length > 0 && paged.items.every((s) => selected.includes(s.id));
  const isPagePartial =
    paged.items.length > 0 &&
    paged.items.some((s) => selected.includes(s.id)) &&
    !isPageSel;
  const totalPages = Math.ceil(selected.length / CONFIG.CARDS_PER_PAGE) * 2;

  const handleSelectAll = () =>
    setSelected(isAllSel ? [] : filtered.map((s) => s.id));
  const handleSelectPage = () => {
    const ids = paged.items.map((s) => s.id);
    setSelected((prev) =>
      isPageSel
        ? prev.filter((id) => !ids.includes(id))
        : [...new Set([...prev, ...ids])]
    );
  };
  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  const handleDownload = () =>
    generate(validStudentData.filter((s) => selected.includes(s.id)));

  if (loading)
    return (
      <Modal open={open} onClose={onClose} size="large">
        <Modal.Header>
          <Icon name="download" /> Bulk Download Student Library Cards
        </Modal.Header>
        <Modal.Content>
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Icon name="spinner" loading size="big" />
            <p>Loading...</p>
          </div>
        </Modal.Content>
      </Modal>
    );

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header>
        <Icon name="download" /> Bulk Download Student Library Cards
      </Modal.Header>
      <Modal.Content scrolling>
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            gap: "20px",
            flexDirection: "row-reverse",
          }}
        >
          <div style={{ flex: 1 }}>
            <p>
              <strong>Total:</strong> {validStudentData?.length || 0}
            </p>
            <p>
              <strong>Selected:</strong> {selected.length}
            </p>
            <p>
              <strong>Pages:</strong> {totalPages} ({CONFIG.CARDS_PER_PAGE}{" "}
              cards/page, {CONFIG.GRID.COLS}x{CONFIG.GRID.ROWS})
            </p>
          </div>
          <div
            style={{
              flex: 1,
              padding: "10px",
              backgroundColor: "#e8f5e9",
              borderLeft: "4px solid #4caf50",
              borderRadius: "4px",
            }}
          >
            <strong>📄 Print Instructions:</strong>
            <ol
              style={{ marginTop: "8px", marginBottom: 0, paddingLeft: "20px" }}
            >
              <li>Print odd pages (fronts)</li>
              <li>Flip paper stack (short edge)</li>
              <li>Print even pages (backs)</li>
              <li>Cards aligned for cutting!</li>
            </ol>
          </div>
        </div>
        <div style={{ marginBottom: "15px", display: "flex", gap: "10px" }}>
          <Input
            icon="search"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ flex: 1 }}
            disabled={isGen}
          />
          <Button
            color={isAllSel ? "red" : "green"}
            onClick={handleSelectAll}
            disabled={isGen || !filtered.length}
          >
            <Icon name={isAllSel ? "remove" : "check"} />{" "}
            {isAllSel ? "Deselect All" : "Select All"}
          </Button>
        </div>
        <div
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid #ddd",
            borderRadius: "4px",
          }}
        >
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={1}>
                  <Checkbox
                    checked={isPageSel}
                    indeterminate={isPagePartial}
                    onChange={handleSelectPage}
                    disabled={isGen || !paged.items.length}
                  />
                </Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Library No</Table.HeaderCell>
                <Table.HeaderCell>Program</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {!filtered.length ? (
                <Table.Row>
                  <Table.Cell
                    colSpan="4"
                    textAlign="center"
                    style={{ padding: "20px", color: "#999" }}
                  >
                    {search ? "No matches" : "No students"}
                  </Table.Cell>
                </Table.Row>
              ) : (
                paged.items.map((s) => (
                  <Table.Row key={s.id}>
                    <Table.Cell>
                      <Checkbox
                        checked={selected.includes(s.id)}
                        onChange={() => toggleSelect(s.id)}
                        disabled={isGen}
                      />
                    </Table.Cell>
                    <Table.Cell>{s.full_name || s.name}</Table.Cell>
                    <Table.Cell>{s.library_no || "N/A"}</Table.Cell>
                    <Table.Cell>{s.program || "N/A"}</Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </div>
        {filtered.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "15px",
            }}
          >
            <div style={{ fontSize: "12px", color: "#666" }}>
              Showing {paged.start + 1}-{Math.min(paged.end, filtered.length)}{" "}
              of {filtered.length}
            </div>

            {paged.totalPages > 1 && (
              <Pagination
                activePage={page}
                totalPages={paged.totalPages}
                onPageChange={(e, { activePage }) => setPage(activePage)}
                size="mini"
                disabled={isGen}
              />
            )}
          </div>
        )}

        {isGen && (
          <div style={{ marginTop: "20px" }}>
            <Progress
              percent={Math.round(progress)}
              indicating
              progress
              size="small"
            />
            <p
              style={{ textAlign: "center", marginTop: "10px", color: "#666" }}
            >
              {status}
            </p>
          </div>
        )}
        {!isGen && selected.length > 0 && (
          <div style={{ marginTop: "20px" }}>
            <p style={{ fontSize: "12px", color: "#666" }}>
              <Icon name="info circle" /> Note: May take several minutes. Backs
              are horizontally reversed for duplex alignment.
            </p>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isGen}>
          Cancel
        </Button>
        <Button
          color="blue"
          onClick={handleDownload}
          disabled={isGen || !selected.length}
          loading={isGen}
        >
          <Icon name="download" />{" "}
          {isGen
            ? "Generating..."
            : `Download ${selected.length} Card${
                selected.length !== 1 ? "s" : ""
              }`}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

BulkStudentCardDownloader.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  studentData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      full_name: PropTypes.string,
      email: PropTypes.string,
      library_no: PropTypes.string,
      department: PropTypes.string,
      image_url: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
};

BulkStudentCardDownloader.defaultProps = {
  studentData: [],
  loading: false,
};

export default BulkStudentCardDownloader;
